import React, { useRef } from "react";
import { Link } from "@/i18n/router";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  ShieldCheck, Eye, Cpu, Landmark, ArrowUpRight, ArrowRight, CheckCircle2, Boxes, Activity, FileCheck2, GitBranch,
} from "lucide-react";
import { Seo } from "@/lib/seo";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { Overline, SectionHeading, PrimaryLink, GhostLink } from "@/components/ui/primitives";
import { architectureFlow } from "@/data/site";
import { solutions } from "@/data/solutions";
import { Emblem } from "@/components/Emblem";
import { api, PLATFORM_URL } from "@/lib/api";
import { BRAND } from "@/data/brand";
import { useLang } from "@/i18n/LanguageContext";

const easeOut = [0.16, 1, 0.3, 1];

const HERO_LINES = ["AstroLife™", "AI Governance", "Operating System"];

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: easeOut, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const ref = useRef(null);
  const { tr } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.06]);

  return (
    <section ref={ref} className="relative min-h-[88svh] overflow-hidden bg-navy pt-[72px]" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid opacity-[0.35]" aria-hidden="true" />
      <div className="radial-glow absolute inset-0" aria-hidden="true" />
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0" aria-hidden="true">
        <img
          src={BRAND.coverWide}
          alt=""
          className="h-full w-full object-cover object-center opacity-[0.3]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-navy/80" />
      </motion.div>

      {/* Signature brand emblem */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.4, ease: easeOut, delay: 0.4 }}
        className="pointer-events-none absolute right-[6%] top-[40%] hidden -translate-y-1/2 lg:block"
        aria-hidden="true"
      >
        <div className="relative">
          <div className="absolute inset-0 blur-3xl bg-astro-blue/20 rounded-full" />
          <Emblem className="relative h-[260px] w-[260px] drop-shadow-[0_0_40px_rgba(10,82,204,0.35)]" spinStars />
        </div>
      </motion.div>

      <div className="relative mx-auto flex min-h-[85svh] lg:min-h-[88svh] max-w-7xl flex-col justify-start px-5 lg:px-8 pt-12 pb-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-astro-blue/30 bg-astro-blue/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-astro-light animate-pulse" />
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-astro-light">
              {tr("Sovereign · Verifiable · European")}
            </span>
          </div>
        </motion.div>

        <h1 className="mt-8 max-w-4xl font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] text-white">
          {HERO_LINES.map((line, i) => (
            <HeroLine key={i} delay={0.15 + i * 0.12}>
              {i === 1 ? <span className="text-astro-light">{tr(line)}</span> : tr(line)}
            </HeroLine>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 0.6 }}
          className="mt-7 max-w-xl text-lg md:text-xl leading-relaxed text-silver/75"
        >
          {tr("Verifiable AI Governance for Enterprises, Governments and Critical Infrastructure.")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: easeOut, delay: 0.75 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <a
            href={PLATFORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-access-platform"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-astro-blue px-8 py-4 font-semibold text-white hover:bg-astro-light transition-colors"
          >
            {tr("Access Platform")} <ArrowUpRight className="h-4 w-4" />
          </a>
          <Link
            to="/private-beta"
            data-testid="hero-request-beta"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/20 px-8 py-4 font-semibold text-white hover:bg-white/10 transition-colors"
          >
            {tr("Request Private Beta")} <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-silver/50"
        >
          <span className="font-semibold text-silver/70">{tr("Aligned with")}</span>
          {["EU AI Act", "ISO/IEC 42001", "ISO/IEC 27001", "NIST AI RMF"].map((f) => (
            <span key={f} className="border-l border-navy-border pl-4 first:border-0 first:pl-0">{f}</span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

const capabilities = [
  { icon: Eye, title: "Auditable AI", text: "Complete traceability and explainability throughout the operational lifecycle." },
  { icon: ShieldCheck, title: "Governance", text: "Human oversight, compliance monitoring and clear accountability mechanisms." },
  { icon: Cpu, title: "Critical Operations", text: "Engineered for strategic, industrial and mission-critical infrastructures." },
  { icon: Landmark, title: "European Sovereignty", text: "Aligned with EU digital sovereignty and regulatory frameworks." },
];

const manifesto = [
  {
    n: "01",
    title: "Our Mission",
    text: "To operationalise verifiable AI governance — turning principles into repeatable controls, clear ownership and durable evidence for the organisations that matter most.",
  },
  {
    n: "02",
    title: "Our Vision",
    text: "A Europe where artificial intelligence is auditable, resilient and sovereign by design — trusted by governments, critical infrastructure and regulated industries.",
  },
  {
    n: "03",
    title: "Our Principle",
    text: "Every AI system accounted for. Every decision with an owner. Every control linked to evidence. Governance you can prove, not merely declare.",
  },
];

const bento = [
  { icon: Activity, title: "Executive Dashboard", span: "md:col-span-2", text: "Real-time posture, maturity and compliance scores for leadership." },
  { icon: Boxes, title: "AI Inventory (AI-BOM)", span: "", text: "Central, versioned register of every model, dataset and dependency." },
  { icon: GitBranch, title: "AI Lifecycle", span: "", text: "Design → validation → deployment → monitoring → retirement." },
  { icon: ShieldCheck, title: "Risk Register", span: "", text: "Persistent identification, classification and mitigation tracking." },
  { icon: FileCheck2, title: "Audit Centre & Evidence", span: "md:col-span-2", text: "Tamper-evident evidence chains for internal and external review." },
];

export default function Home() {
  const { tr } = useLang();
  const { data } = useQuery({
    queryKey: ["home-posts"],
    queryFn: async () => (await api.get("/blog/posts?limit=3")).data,
  });
  const posts = data?.posts || [];

  return (
    <>
      <Seo
        title={tr("Sovereign AI Infrastructure & Verifiable AI Governance")}
        description={tr("AstroLife™ AI Governance OS — verifiable AI governance for enterprises, governments and critical infrastructure. EU AI Act readiness, AI inventory, risk register and audit centre.")}
        path="/"
        keywords="European AI, AI Governance, AI Governance Platform, AI Governance OS, EU AI Act, Sovereign AI, Verifiable AI, Trustworthy AI"
      />

      <Hero />

<section className="sr-only" aria-hidden="false">
  <h2>Enterprise AI Governance Platform</h2>

  <p>
    AstroLife AI Governance OS is a sovereign enterprise platform for AI
    governance, regulatory compliance, AI lifecycle management, AI inventory,
    model risk management, audit readiness and trustworthy artificial
    intelligence. The platform enables organizations to identify, classify,
    document, monitor and govern AI systems throughout their complete lifecycle.
  </p>

  <p>
    Designed for governments, public administrations, critical infrastructure,
    healthcare, finance, defence, aerospace, energy and industrial
    organizations, AstroLife provides verifiable AI governance aligned with the
    European AI Act, GDPR, NIS2, ISO 42001 and internationally recognized
    governance frameworks. The platform integrates AI inventory, governance
    workflows, explainability, evidence management, risk assessment, compliance
    reporting, policy management and executive dashboards.
  </p>

  <p>
    AstroLife AI Governance OS supports responsible artificial intelligence by
    combining transparency, traceability, accountability, resilience and
    operational governance. Organizations can continuously evaluate compliance,
    maintain audit-ready documentation, monitor AI systems, reduce operational
    risk and accelerate digital transformation while preserving technological
    sovereignty and regulatory confidence.
  </p>
</section>


      {/* Core capabilities */}
      <section className="bg-navy py-24 lg:py-32" aria-labelledby="capabilities-heading">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading
            overline={tr("Core Capabilities")}
            title={tr("Artificial intelligence, governance, orchestration and verification in a single operational framework.")}
          />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {capabilities.map((c) => (
              <motion.div
                key={c.title}
                variants={staggerItem}
                className="group rounded-2xl border border-navy-border bg-navy-surface p-7 transition-transform duration-300 hover:-translate-y-1.5 hover:border-astro-blue/40"
                data-testid={`capability-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light group-hover:bg-astro-blue group-hover:text-white transition-colors">
                  <c.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 font-heading text-xl font-semibold text-white">{tr(c.title)}</h3>
                <p className="mt-3 text-sm leading-relaxed text-silver/60">{tr(c.text)}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Manifesto */}
      <section className="border-y border-navy-border bg-navy-surface py-24 lg:py-32" id="manifesto">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Overline>{tr("Manifesto")}</Overline>
          <div className="mt-12 space-y-16">
            {manifesto.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.05}>
                <div className="relative grid items-start gap-6 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <span className="font-heading text-7xl md:text-8xl font-black text-outline">{m.n}</span>
                  </div>
                  <div className="md:col-span-8">
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white">{tr(m.title)}</h3>
                    <p className="mt-4 max-w-2xl text-lg leading-relaxed text-silver/70">{tr(m.text)}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Operational architecture marquee */}
      <section className="bg-navy py-20" aria-label="Operational architecture flow">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Operational Architecture")} title={tr("Data-driven orchestration with integrated governance and auditability.")} />
        </div>
        <div className="mt-12">
          <Marquee items={architectureFlow} />
        </div>
      </section>

      {/* Flagship OS bento */}
      <section className="bg-navy pb-24 lg:pb-32" aria-labelledby="flagship-heading">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              overline={tr("Flagship Platform")}
              title="AstroLife™ AI Governance OS"
              sub={tr("An enterprise operating system that unifies AI inventory, risk, lifecycle, audit readiness and evidence — designed for governments, critical infrastructure and regulated industries.")}
            />
            <div className="flex-shrink-0">
              <GhostLink to="/ai-governance-os">{tr("Explore the platform")}</GhostLink>
            </div>
          </div>

          <Stagger className="mt-14 grid gap-4 md:grid-cols-3">
            {bento.map((b) => (
              <motion.div
                key={b.title}
                variants={staggerItem}
                className={`rounded-2xl border border-navy-border bg-navy-surface p-7 transition-colors hover:border-astro-blue/40 ${b.span}`}
              >
                <b.icon className="h-7 w-7 text-astro-light" />
                <h3 className="mt-5 font-heading text-lg font-semibold text-white">{tr(b.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-silver/60">{tr(b.text)}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />

      {/* Solutions preview */}
      <section className="bg-navy py-24 lg:py-32" aria-labelledby="solutions-heading">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Strategic Domains")} title={tr("Governance for the sectors that keep Europe running.")} />
          <Stagger className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {solutions.map((s) => (
              <motion.div key={s.slug} variants={staggerItem}>
                <Link
                  to={`/solutions/${s.slug}`}
                  data-testid={`solution-card-${s.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-navy-border bg-navy-surface p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-astro-blue/40"
                >
                  <s.icon className="h-7 w-7 text-astro-light" />
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{tr(s.name)}</h3>
                  <p className="mt-1 text-xs uppercase tracking-wider text-silver/40">{tr(s.tagline)}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-astro-light opacity-0 transition-opacity group-hover:opacity-100">
                    {tr("Explore")} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Latest insights */}
      {posts.length > 0 && (
        <section className="border-t border-navy-border bg-navy-surface py-24 lg:py-32" aria-labelledby="insights-heading">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <div className="flex items-end justify-between">
              <SectionHeading overline={tr("Insights")} title={tr("Latest from the AstroLife journal")} />
              <GhostLink to="/blog" className="hidden sm:inline-flex">{tr("View all")}</GhostLink>
            </div>
            <div className="mt-14 grid gap-6 md:grid-cols-3">
              {posts.map((p) => (
                <Reveal key={p.id}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full overflow-hidden rounded-2xl border border-navy-border bg-navy">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.cover_image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-semibold uppercase tracking-wider text-astro-light">{p.category}</span>
                      <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-white group-hover:text-astro-light transition-colors">{p.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-silver/60">{p.excerpt}</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
